import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Users, ArrowLeft, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const emptyMember = () => ({ name: "", email: "", phone: "" });

/**
 * Group Booking — owner-side creation page.
 *
 * Auth gate: anonymous visitors get bounced to /register?next=/groups/new so
 * they can create the owning account first; signed-in clients see the form
 * immediately. The form collects basic trip context (name, destination,
 * window) plus a roster of invitees. On submit each invitee receives an
 * email with a personalised sign-up link.
 */
export default function CreateGroupPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [form, setForm] = useState({
    name: "",
    destination: "",
    travel_window: "",
    notes: "",
  });
  const [members, setMembers] = useState([emptyMember(), emptyMember()]);
  const [submitting, setSubmitting] = useState(false);

  // Bounce to sign-up if not authenticated. Done in a useEffect so the
  // navigation is a side-effect, not a render-time mutation (which causes
  // a blank-page flash). After /register the `next=` param brings them back.
  useEffect(() => {
    if (!loading && !user) {
      navigate("/register?next=/groups/new", { replace: true });
    }
  }, [loading, user, navigate]);

  // While auth is loading OR while waiting for the effect-driven redirect
  // to fire, show a friendly placeholder rather than a blank canvas.
  if (loading || !user) {
    return (
      <div
        className="max-w-md mx-auto px-6 py-20 text-center"
        data-testid="create-group-loading"
      >
        <Loader2 size={28} className="animate-spin text-[var(--gold)] mx-auto" />
        <h1 className="font-display text-2xl text-[var(--emerald)] mt-5">
          {loading ? "Loading…" : "One moment — taking you to sign-up."}
        </h1>
        {!loading && !user && (
          <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">
            Group trips need an owner account so your companions know who
            invited them. We'll bring you right back here after you set
            things up.
          </p>
        )}
      </div>
    );
  }

  const updateMember = (i, patch) => {
    setMembers((arr) => arr.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  };
  const addMember = () => setMembers((arr) => [...arr, emptyMember()]);
  const removeMember = (i) =>
    setMembers((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  const validMembers = members.filter((m) => m.email.trim() && m.name.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Give your group a name.");
      return;
    }
    if (!validMembers.length) {
      toast.error("Add at least one travel companion.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/groups", {
        ...form,
        members: validMembers.map((m) => ({
          name: m.name.trim(),
          email: m.email.trim().toLowerCase(),
          phone: m.phone.trim() || undefined,
        })),
      });
      toast.success(
        `Group created · ${r.data.invite_count} invitation${r.data.invite_count === 1 ? "" : "s"} sent.`
      );
      navigate(`/groups/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create the group.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="create-group-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-[0.18em] text-[var(--ink-2)] hover:text-[var(--emerald)] inline-flex items-center gap-2"
        data-testid="create-group-back-btn"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Back
      </button>

      <div className="mt-6">
        <div className="overline flex items-center gap-2">
          <Users size={14} strokeWidth={1.5} className="text-[var(--gold)]" />
          Group booking
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-2 leading-tight">
          Travel together — beautifully coordinated.
        </h1>
        <p className="text-base text-[var(--ink-2)] mt-4 max-w-xl leading-relaxed">
          Build the trip once, invite your travel companions, and they'll receive
          an elegant email to view the itinerary you're crafting with Sasha and
          our concierge team.
        </p>
      </div>

      <form onSubmit={submit} className="mt-10 lux-card !p-8" data-testid="create-group-form">
        <div className="space-y-5">
          <div>
            <label className="overline">Group name *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Paris Spring Crew"
              className="form-input mt-2 w-full"
              data-testid="create-group-name-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="overline">Destination</label>
              <input
                type="text"
                value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })}
                placeholder="Paris, France"
                className="form-input mt-2 w-full"
                data-testid="create-group-destination-input"
              />
            </div>
            <div>
              <label className="overline">Travel window</label>
              <input
                type="text"
                value={form.travel_window}
                onChange={(e) => setForm({ ...form, travel_window: e.target.value })}
                placeholder="April 15-22, 2026"
                className="form-input mt-2 w-full"
                data-testid="create-group-window-input"
              />
            </div>
          </div>
          <div>
            <label className="overline">Notes for the group</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything you'd like everyone to know — dietary needs, accessibility, occasion details…"
              className="form-input mt-2 w-full"
              data-testid="create-group-notes-input"
            />
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="overline">Travel companions</div>
              <p className="text-xs text-[var(--ink-2)] mt-1">
                Each person receives an invitation email to set up their own
                view of this trip.
              </p>
            </div>
            <button
              type="button"
              onClick={addMember}
              className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] inline-flex items-center gap-1.5"
              data-testid="create-group-add-member-btn"
            >
              <Plus size={14} strokeWidth={1.5} /> Add another
            </button>
          </div>

          <div className="space-y-3">
            {members.map((m, i) => (
              <div
                key={m.id || `member-${i}`}
                className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-start p-3 border border-[var(--emerald)]/15 bg-[var(--ivory-2)]"
                data-testid={`create-group-member-row-${i}`}
              >
                <input
                  type="text"
                  placeholder="Full name"
                  value={m.name}
                  onChange={(e) => updateMember(i, { name: e.target.value })}
                  className="form-input"
                  data-testid={`create-group-member-name-${i}`}
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={m.email}
                  onChange={(e) => updateMember(i, { email: e.target.value })}
                  className="form-input"
                  data-testid={`create-group-member-email-${i}`}
                />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={m.phone}
                  onChange={(e) => updateMember(i, { phone: e.target.value })}
                  className="form-input"
                  data-testid={`create-group-member-phone-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeMember(i)}
                  className="text-[var(--muted)] hover:text-[var(--brand-red)] self-center px-2"
                  aria-label="Remove member"
                  data-testid={`create-group-member-remove-${i}`}
                >
                  <Trash2 size={16} strokeWidth={1.25} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 items-center justify-between">
          <p className="text-xs text-[var(--ink-2)] inline-flex items-center gap-2">
            <Mail size={14} strokeWidth={1.25} className="text-[var(--gold)]" />
            {validMembers.length === 0
              ? "No invitations yet."
              : `${validMembers.length} invitation${validMembers.length === 1 ? "" : "s"} will be sent.`}
          </p>
          <button
            type="submit"
            disabled={submitting || validMembers.length === 0}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="create-group-submit-btn"
          >
            {submitting ? "Creating group…" : "Create group & send invitations"}
          </button>
        </div>
      </form>
    </div>
  );
}
